import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config/apiconfig';
import AdminAssignModal from '../components/AdminAssignModal';

function Section({ title, children }) {
  return (
    <section className="bg-white rounded-xl shadow p-6 mb-8">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      {children}
    </section>
  );
}

export default function AdminCompanies() {
  const [tab, setTab] = useState('companies'); // companies | clusters | units
  const [companyQuery, setCompanyQuery] = useState('');
  const debouncedQuery = useMemo(() => companyQuery.trim(), [companyQuery]);
  const [companyResults, setCompanyResults] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [companiesList, setCompaniesList] = useState([]);

  const [companyLogoFile, setCompanyLogoFile] = useState(null);
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const [companyIndustry, setCompanyIndustry] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyEmployeeCount, setCompanyEmployeeCount] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [isEditingCompanyDetails, setIsEditingCompanyDetails] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [nameCheckStatus, setNameCheckStatus] = useState('idle'); // idle|checking|ok|duplicate
  const [nameCheckMsg, setNameCheckMsg] = useState('');

  const [existingClusters, setExistingClusters] = useState([]);
  const [existingUnits, setExistingUnits] = useState([]);
  const [editUnitId, setEditUnitId] = useState(null);
  const [editUnitAddress, setEditUnitAddress] = useState('');
  const [editUnitPhone, setEditUnitPhone] = useState('');
  const [newClusterName, setNewClusterName] = useState('');
  const [newUnitName, setNewUnitName] = useState('');
  const [newUnitClusterId, setNewUnitClusterId] = useState('');
  const [newUnitAddress, setNewUnitAddress] = useState('');
  const [newUnitPhone, setNewUnitPhone] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isExistingCompany = !!selectedCompany?.id;
  const isNewCompany = !selectedCompany?.id;
  const newCompanyFieldsValid = useMemo(() => {
    if (!isNewCompany) return false;
    // Only Company Name is strictly required; others are optional per schema
    return !!companyName.trim();
  }, [isNewCompany, companyName]);

  // Real-time uniqueness validation for companyName inside modal
  useEffect(() => {
    if (!showCompanyModal) return;
    const name = companyName.trim();
    if (name.length < 2) { setNameCheckStatus('idle'); setNameCheckMsg(''); return; }
    let ac = new AbortController();
    (async () => {
      setNameCheckStatus('checking'); setNameCheckMsg('');
      try {
        const res = await fetch(`${API_BASE_URL}/api/companies/search?query=${encodeURIComponent(name)}`, { signal: ac.signal });
        if (!res.ok) { setNameCheckStatus('idle'); return; }
        const list = await res.json();
        const exact = Array.isArray(list) && list.some(c => String(c.company_name).toLowerCase() === name.toLowerCase());
        // Allow keeping the same name in edit mode
        const sameAsCurrent = !!selectedCompany?.id && name.toLowerCase() === String(selectedCompany?.company_name || '').toLowerCase();
        if (exact && !sameAsCurrent) {
          setNameCheckStatus('duplicate');
          setNameCheckMsg('A company with this name already exists.');
        } else {
          setNameCheckStatus('ok'); setNameCheckMsg('');
        }
      } catch { setNameCheckStatus('idle'); }
    })();
    return () => ac.abort();
  }, [showCompanyModal, companyName, selectedCompany?.id, selectedCompany?.company_name]);

  // Load all companies for Companies tab and dropdowns
  async function loadCompaniesList() {
    try {
      const res = await fetch(`${API_BASE_URL}/api/companies`);
      if (res.ok) {
        const data = await res.json();
        setCompaniesList(Array.isArray(data) ? data : []);
      } else {
        setCompaniesList([]);
      }
  async function removeClusterAdmin(c) {
    if (!selectedCompany?.id || !c?.id) return;
    if (!window.confirm('Remove cluster admin?')) return;
    setError(''); setSuccess('');
    try {
      const companyParam = selectedCompany.key || selectedCompany.id;
      const res = await fetch(`${API_BASE_URL}/api/companies/${companyParam}/clusters/${c.id}/admin`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove cluster admin');
      setSuccess('Cluster admin removed');
      await refreshLists();
    } catch (e) { setError(e.message || 'Failed to remove'); }
  }
    } catch {
      setCompaniesList([]);
    }
  }

  async function removeCompanyAdmin(c) {
    if (!c?.id) return;
    if (!window.confirm('Remove company admin?')) return;
    setError(''); setSuccess('');
    try {
      const companyParam = c.company_key || c.id;
      const res = await fetch(`${API_BASE_URL}/api/companies/${companyParam}/admin`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove company admin');
      setSuccess('Company admin removed');
      await loadCompaniesList();
      await refreshLists();
    } catch (e) { setError(e.message || 'Failed to remove'); }
  }

  useEffect(() => { loadCompaniesList(); }, []);

  useEffect(() => {
    let ac = new AbortController();
    (async () => {
      if (debouncedQuery.length < 2) { setCompanyResults([]); return; }
      try {
        const res = await fetch(`${API_BASE_URL}/api/companies/search?query=${encodeURIComponent(debouncedQuery)}`, { signal: ac.signal });
        if (!res.ok) return;
        const data = await res.json();
        setCompanyResults(data || []);
      } catch {}
    })();
    return () => ac.abort();
  }, [debouncedQuery]);

  useEffect(() => {
    (async () => {
      if (!selectedCompany?.id) { setExistingClusters([]); setExistingUnits([]); return; }
      try {
        const companyParam = selectedCompany.key || selectedCompany.id;
        const [clRes, unRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/companies/${companyParam}/clusters`),
          fetch(`${API_BASE_URL}/api/companies/${companyParam}/units`),
        ]);
        if (clRes.ok) setExistingClusters(await clRes.json()); else setExistingClusters([]);
        if (unRes.ok) setExistingUnits(await unRes.json()); else setExistingUnits([]);
      } catch {
        setExistingClusters([]);
        setExistingUnits([]);
      }
    })();
  }, [selectedCompany?.id]);

  // Sync companyName when selection changes or when starting a new company
  useEffect(() => {
    if (selectedCompany?.id) {
      setCompanyName(selectedCompany.company_name || '');
    } else if (isNewCompany) {
      setCompanyName(debouncedQuery);
    } else {
      setCompanyName('');
    }
  }, [selectedCompany?.id, selectedCompany?.company_name, debouncedQuery, isNewCompany]);

  function clusterNameById(id) {
    const c = existingClusters.find(c => String(c.id) === String(id));
    return c?.name || '';
  }

  async function refreshLists() {
    if (!selectedCompany?.id) return;
    try {
      const companyParam = selectedCompany.key || selectedCompany.id;
      const [clRes, unRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/companies/${companyParam}/clusters`),
        fetch(`${API_BASE_URL}/api/companies/${companyParam}/units`),
      ]);
      if (clRes.ok) setExistingClusters(await clRes.json());
      if (unRes.ok) setExistingUnits(await unRes.json());
    } catch {}
  }

  async function addCluster() {
    if (!selectedCompany?.id) return;
    const name = newClusterName.trim();
    if (!name) return;
    try {
      const companyParam = selectedCompany.key || selectedCompany.id;
      const res = await fetch(`${API_BASE_URL}/api/companies/${companyParam}/clusters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        setNewClusterName('');
        await refreshLists();
        await loadCompaniesList();
      }
    } catch {}
  }

  async function addUnit() {
    if (!selectedCompany?.id) return;
    const name = newUnitName.trim();
    if (!name) return;
    try {
      const companyParam = selectedCompany.key || selectedCompany.id;
      const res = await fetch(`${API_BASE_URL}/api/companies/${companyParam}/units`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, cluster_id: newUnitClusterId ? Number(newUnitClusterId) : null, address: newUnitAddress || null, phone: newUnitPhone || null }),
      });
      if (res.ok) {
        setNewUnitName('');
        setNewUnitClusterId('');
        setNewUnitAddress('');
        setNewUnitPhone('');
        await refreshLists();
        await loadCompaniesList();
      }
    } catch {}
  }

  function startEditUnit(u) {
    setEditUnitId(u.id);
    setEditUnitAddress(u.address || '');
    setEditUnitPhone(u.phone || '');
  }
  function cancelEditUnit() {
    setEditUnitId(null);
    setEditUnitAddress('');
    setEditUnitPhone('');
  }
  async function saveEditUnit(u) {
    if (!selectedCompany?.id || !u?.id) return;
    try {
      const companyParam = selectedCompany.key || selectedCompany.id;
      const res = await fetch(`${API_BASE_URL}/api/companies/${companyParam}/units/${u.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: u.name, address: editUnitAddress || null, phone: editUnitPhone || null, cluster_id: u.cluster_id || null }),
      });
      if (res.ok) {
        await refreshLists();
        cancelEditUnit();
      }
    } catch {}
  }

  async function deleteUnit(u) {
    if (!selectedCompany?.id || !u?.id) return;
    if (!window.confirm(`Delete unit "${u.name}"?`)) return;
    try {
      const companyParam = selectedCompany.key || selectedCompany.id;
      const res = await fetch(`${API_BASE_URL}/api/companies/${companyParam}/units/${u.id}`, { method: 'DELETE' });
      if (res.ok) await refreshLists();
    } catch {}
  }

  async function deleteClusterById(clusterId) {
    if (!selectedCompany?.id || !clusterId) return;
    const name = clusterNameById(clusterId) || 'this cluster';
    if (!window.confirm(`Delete cluster "${name}"?`)) return;
    try {
      const companyParam = selectedCompany.key || selectedCompany.id;
      const res = await fetch(`${API_BASE_URL}/api/companies/${companyParam}/clusters/${clusterId}`, { method: 'DELETE' });
      if (res.ok) await refreshLists();
    } catch {}
  }

  useEffect(() => {
    if (!selectedCompany) return;
    const q = companyQuery.trim();
    const sel = (selectedCompany.company_name || '').trim();
    if (q && sel && q.toLowerCase() !== sel.toLowerCase()) setSelectedCompany(null);
  }, [companyQuery]);

  useEffect(() => {
    (async () => {
      if (!selectedCompany?.id) return;
      try {
        const res = await fetch(`${API_BASE_URL}/api/companies/${selectedCompany.id}`);
        if (res.ok) {
          const c = await res.json();
          setCompanyIndustry(c.industry || '');
          setCompanyWebsite(c.website || '');
          setCompanyEmployeeCount(c.employee_count || '');
          setCompanyDescription(c.description || '');
          setCompanyPhone(c.phone || c.company_phone || '');
          setCompanyAddress(c.address || '');
          setCompanyLogoUrl(c.logo ? `${API_BASE_URL}/api/companies/${selectedCompany.id}/logo` : '');
          setIsEditingCompanyDetails(false);
        }
      } catch {}
    })();
  }, [selectedCompany?.id]);

  async function createCompanyAndSetup() {
    setError('');
    setSuccess('');
    if (!isNewCompany) { setError('Enter a new company name (min 2 chars)'); return; }
    if (!newCompanyFieldsValid) { setError('Please complete all company fields, including Company Name.'); return; }
    const nameToCreate = companyName.trim();
    // Uniqueness check by exact name (case-insensitive)
    try {
      const chk = await fetch(`${API_BASE_URL}/api/companies/search?query=${encodeURIComponent(nameToCreate)}`);
      if (chk.ok) {
        const list = await chk.json();
        const exists = Array.isArray(list) && list.some(c => String(c.company_name).toLowerCase() === nameToCreate.toLowerCase());
        if (exists) {
          setError('A company with this name already exists. Please choose a different name.');
          return;
        }
      }
    } catch {}
    setSaving(true);
    try {
      const fd = new FormData();
      if (companyLogoFile) fd.append('logo', companyLogoFile);
      fd.append('name', nameToCreate);
      fd.append('location', companyAddress);
      fd.append('description', companyDescription);
      fd.append('link', companyWebsite);
      const res = await fetch(`${API_BASE_URL}/api/companies`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Failed to create company');
      const created = await res.json();
      const newId = created.companyId || created.id;
      if (!newId) throw new Error('Company created but ID missing in response');

      try {
        const companyPayload = {
          company_name: nameToCreate,
          industry: companyIndustry,
          website: companyWebsite,
          description: companyDescription,
          phone: companyPhone,
          address: companyAddress,
          employee_count: companyEmployeeCount,
        };
        await fetch(`${API_BASE_URL}/api/company/${newId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(companyPayload) });
      } catch {}

      setSelectedCompany({ id: newId, company_name: nameToCreate });
      setSuccess('Company created successfully');
      await loadCompaniesList();
    } catch (e) {
      setError(e.message || 'Failed to create company');
    } finally {
      setSaving(false);
    }
  }

  async function saveExistingCompanyUpdates() {
    if (!selectedCompany?.id) return;
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const nameToSave = (companyName || '').trim();
      if (!nameToSave) throw new Error('Company Name is required.');
      if (nameToSave.toLowerCase() !== String(selectedCompany.company_name || '').toLowerCase()) {
        try {
          const chk = await fetch(`${API_BASE_URL}/api/companies/search?query=${encodeURIComponent(nameToSave)}`);
          if (chk.ok) {
            const list = await chk.json();
            const exists = Array.isArray(list) && list.some(c => String(c.company_name).toLowerCase() === nameToSave.toLowerCase());
            if (exists) {
              throw new Error('A company with this name already exists. Please choose a different name.');
            }
          }
        } catch (e) { if (e instanceof Error) { throw e; } }
      }
      const companyPayload = {
        company_name: nameToSave,
        industry: companyIndustry,
        website: companyWebsite,
        description: companyDescription,
        phone: companyPhone,
        address: companyAddress,
        employee_count: companyEmployeeCount,
      };
      const r = await fetch(`${API_BASE_URL}/api/company/${selectedCompany.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(companyPayload) });
      if (!r.ok) throw new Error('Failed to update company');
      setSuccess('Company details updated');
      setSelectedCompany(prev => prev ? ({ ...prev, company_name: nameToSave }) : prev);
    } catch (e) {
      setError(e.message || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  }

  async function uploadExistingCompanyLogo() {
    if (!selectedCompany?.id || !companyLogoFile) { setError('Please choose a logo file first.'); return; }
    setError('');
    setSuccess('');
    try {
      const fd = new FormData();
      fd.append('logo', companyLogoFile);
      const res = await fetch(`${API_BASE_URL}/api/companies/${selectedCompany.id}/logo`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Failed to upload logo');
      setSuccess('Logo uploaded successfully');
    } catch (e) {
      setError(e.message || 'Logo upload failed');
    }
  }

  // Assign Admin modal state and handlers
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // { scope: 'company'|'cluster'|'unit', companyId, clusterId?, unitId?, initial }

  function openAssignForCompany(c) {
    setAssignTarget({
      scope: 'company',
      companyId: c.company_key || c.id,
      initial: {
        name: c.company_admin_name || '',
        phone: c.company_admin_phone || '',
        email: c.company_admin_email || ''
      }
    });
    setAssignOpen(true);
  }
  function openAssignForCluster(c) {
    setAssignTarget({
      scope: 'cluster',
      companyId: selectedCompany?.key || selectedCompany?.id,
      clusterId: c.id,
      initial: {
        name: c.cluster_admin_name || '',
        phone: c.cluster_admin_phone || '',
        email: c.cluster_admin_email || ''
      }
    });
    setAssignOpen(true);
  }
  function openAssignForUnit(u) {
    setAssignTarget({
      scope: 'unit',
      companyId: selectedCompany?.key || selectedCompany?.id,
      unitId: u.id,
      initial: {
        name: u.unit_admin_name || '',
        phone: u.unit_admin_phone || '',
        email: u.unit_admin_email || ''
      }
    });
    setAssignOpen(true);
  }

  async function removeClusterAdmin(c) {
    if (!selectedCompany?.id || !c?.id) return;
    if (!window.confirm('Remove cluster admin?')) return;
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/companies/${selectedCompany.id}/clusters/${c.id}/admin`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove cluster admin');
      setSuccess('Cluster admin removed');
      await refreshLists();
    } catch (e) { setError(e.message || 'Failed to remove'); }
  }

  async function removeUnitAdmin(u) {
    if (!selectedCompany?.id || !u?.id) return;
    if (!window.confirm('Remove unit admin?')) return;
    setError(''); setSuccess('');
    try {
      const companyParam = selectedCompany.key || selectedCompany.id;
      const res = await fetch(`${API_BASE_URL}/api/companies/${companyParam}/units/${u.id}/admin`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove unit admin');
      setSuccess('Unit admin removed');
      await refreshLists();
    } catch (e) { setError(e.message || 'Failed to remove'); }
  }

  async function submitAssignAdmin(values) {
    if (!assignTarget) return;
    setAssignSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: values.name,
        phone: values.phone,
        email: values.email,
        password: values.password,
      };
      let url = '';
      if (assignTarget.scope === 'company') {
        url = `${API_BASE_URL}/api/companies/${assignTarget.companyId}/admin`;
      } else if (assignTarget.scope === 'cluster') {
        url = `${API_BASE_URL}/api/companies/${assignTarget.companyId}/clusters/${assignTarget.clusterId}/admin`;
      } else if (assignTarget.scope === 'unit') {
        url = `${API_BASE_URL}/api/companies/${assignTarget.companyId}/units/${assignTarget.unitId}/admin`;
      }
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to assign admin');
      setSuccess('Admin assigned successfully');
      // Refresh lists
      await loadCompaniesList();
      await refreshLists();
      setAssignOpen(false);
    } catch (e) {
      setError(e.message || 'Failed to assign admin');
    } finally {
      setAssignSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Local navbar to switch between Companies / Clusters / Units */}
      <div className="bg-white rounded-xl shadow p-2 flex gap-2 sticky top-0 z-10">
        <button onClick={()=>setTab('companies')} className={`px-4 py-2 rounded ${tab==='companies'?'bg-indigo-600 text-white':'bg-gray-100'}`}>Companies</button>
        <button onClick={()=>setTab('clusters')} className={`px-4 py-2 rounded ${tab==='clusters'?'bg-indigo-600 text-white':'bg-gray-100'}`}>Clusters</button>
        <button onClick={()=>setTab('units')} className={`px-4 py-2 rounded ${tab==='units'?'bg-indigo-600 text-white':'bg-gray-100'}`}>Units</button>
      </div>

      {tab==='companies' && (
      <Section title="Companies">
        {error && <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200 mb-3">{error}</div>}
        {success && <div className="p-3 rounded bg-green-50 text-green-700 border border-green-200 mb-3">{success}</div>}
        {/* Companies header with Add button */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-base text-gray-700">Manage companies. Select a row to view/edit details.</div>
          <button type="button" className="px-3 py-2 rounded bg-indigo-600 text-white" onClick={()=>{ setSelectedCompany({ id: null, company_name: '' }); setCompanyQuery(''); setCompanyName(''); setCompanyIndustry(''); setCompanyWebsite(''); setCompanyEmployeeCount(''); setCompanyDescription(''); setCompanyPhone(''); setCompanyAddress(''); setCompanyLogoFile(null); setCompanyLogoUrl(''); setIsEditingCompanyDetails(true); setShowCompanyModal(true); }}>Add Company</button>
        </div>

        {/* Companies table */}
        <div className="overflow-x-auto rounded border">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left border">Name</th>
                <th className="px-3 py-2 text-left border">Industry</th>
                <th className="px-3 py-2 text-left border">Website</th>
                <th className="px-3 py-2 text-left border">Phone</th>
                <th className="px-3 py-2 text-left border">Employees</th>
                <th className="px-3 py-2 text-left border">Admin</th>
                <th className="px-3 py-2 text-left border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companiesList.map(c => (
                <tr key={c.id} className="odd:bg-white even:bg-gray-50 hover:bg-indigo-50">
                  <td className="px-3 py-2 border cursor-pointer" onClick={async ()=>{ setSelectedCompany({ id: c.id, company_name: c.company_name }); setCompanyQuery(c.company_name); }}>
                    {c.company_name}
                  </td>
                  <td className="px-3 py-2 border">{c.industry || '-'}</td>
                  <td className="px-3 py-2 border">{c.website || '-'}</td>
                  <td className="px-3 py-2 border">{c.phone || '-'}</td>
                  <td className="px-3 py-2 border">{c.employee_count || '-'}</td>
                  <td className="px-3 py-2 border">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{c.company_admin_name || '-'}</span>
                      <button className="px-2 py-1 text-sm bg-gray-100 rounded text-gray-700" onClick={()=>openAssignForCompany(c)}>Assign</button>
                      {c.company_admin_name && (
                        <button className="px-2 py-1 text-sm bg-red-50 text-red-700 rounded" onClick={()=>removeCompanyAdmin(c)}>Remove</button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 border">
                    <button className="px-2 py-1 text-sm bg-blue-600 text-white rounded mr-2" onClick={()=>{ setSelectedCompany({ id: c.id, company_name: c.company_name }); setCompanyName(c.company_name || ''); setIsEditingCompanyDetails(true); setShowCompanyModal(true); }}>Edit</button>
                  </td>
                </tr>
              ))}
              {companiesList.length === 0 && (
                <tr>
                  <td className="px-3 py-4 text-center text-gray-500 border" colSpan={7}>No companies found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Inline form removed; using modal below */}
      </Section>
      )}

      {tab==='clusters' && (
        <Section title="Clusters">
          {/* Company selector */}
          <div className="mb-4 flex items-center gap-2">
            <label className="text-sm text-gray-600">Company</label>
            <select className="border rounded p-2" value={selectedCompany?.id || ''} onChange={(e)=>{
              const id = e.target.value ? Number(e.target.value) : null;
              const c = companiesList.find(x=>String(x.id)===String(id));
              if (c) { setSelectedCompany({ id: c.id, company_name: c.company_name }); }
            }}>
              <option value="">Select company</option>
              {companiesList.map(c=> (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
          </div>

          {selectedCompany?.id ? (
            <>
              {/* Add Cluster */}
              <div className="space-y-2 mb-4">
                <div className="font-medium">Add Cluster</div>
                <div className="flex gap-2 max-w-xl">
                  <input className="border rounded p-2 flex-1" placeholder="Cluster name" value={newClusterName} onChange={e=>setNewClusterName(e.target.value)} />
                  <button className="px-3 py-2 bg-indigo-600 text-white rounded" onClick={addCluster}>Add</button>
                </div>
                <div className="text-sm text-gray-500">Clusters are not editable. You can delete if no longer needed.</div>
              </div>

              {/* Clusters Table */}
              <div className="overflow-x-auto rounded border">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left border">Name</th>
                      <th className="px-3 py-2 text-left border">Admin</th>
                      <th className="px-3 py-2 text-left border">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingClusters.map(c => (
                      <tr key={c.id} className="odd:bg-white even:bg-gray-50">
                        <td className="px-3 py-2 border">{c.name}</td>
                        <td className="px-3 py-2 border">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">{c.cluster_admin_name || '-'}</span>
                            <button className="px-2 py-1 text-sm bg-gray-100 rounded text-gray-700" onClick={()=>openAssignForCluster(c)}>Assign</button>
                            {c.cluster_admin_name && (
                              <button className="px-2 py-1 text-sm bg-red-50 text-red-700 rounded" onClick={()=>removeClusterAdmin(c)}>Remove</button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 border">
                          <button className="px-2 py-1 text-sm bg-red-600 text-white rounded" onClick={()=>deleteClusterById(c.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                    {existingClusters.length === 0 && (
                      <tr>
                        <td className="px-3 py-4 text-center text-gray-500 border" colSpan={3}>No clusters found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-gray-500">Select a company to manage clusters.</div>
          )}
        </Section>
      )}

      {tab==='units' && (
        <Section title="Units">
          {/* Company selector */}
          <div className="mb-4 flex items-center gap-2">
            <label className="text-sm text-gray-600">Company</label>
            <select className="border rounded p-2" value={selectedCompany?.id || ''} onChange={(e)=>{
              const id = e.target.value ? Number(e.target.value) : null;
              const c = companiesList.find(x=>String(x.id)===String(id));
              if (c) { setSelectedCompany({ id: c.id, company_name: c.company_name }); }
            }}>
              <option value="">Select company</option>
              {companiesList.map(c=> (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
          </div>

          {selectedCompany?.id ? (
            <>
              {/* Add Unit */}
              <div className="space-y-2 mb-3">
                <div className="font-medium">Add Unit</div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2 max-w-5xl">
                  <input className="border rounded p-2" placeholder="Unit name" value={newUnitName} onChange={e=>setNewUnitName(e.target.value)} />
                  <select className="border rounded p-2" value={newUnitClusterId} onChange={e=>setNewUnitClusterId(e.target.value)}>
                    <option value="">No cluster</option>
                    {existingClusters.map(c=> (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <input className="border rounded p-2" placeholder="Unit phone (optional)" value={newUnitPhone} onChange={e=>setNewUnitPhone(e.target.value)} />
                  <input className="border rounded p-2 md:col-span-2" placeholder="Unit address (optional)" value={newUnitAddress} onChange={e=>setNewUnitAddress(e.target.value)} />
                </div>
                <div>
                  <button className="mt-2 px-3 py-2 bg-indigo-600 text-white rounded" onClick={addUnit}>Add Unit</button>
                </div>
              </div>

              {/* Units Table */}
              <div className="overflow-x-auto rounded border">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border px-3 py-2 text-left">Unit</th>
                      <th className="border px-3 py-2 text-left">Cluster</th>
                      <th className="border px-3 py-2 text-left">Phone</th>
                      <th className="border px-3 py-2 text-left">Address</th>
                      <th className="border px-3 py-2 text-left">Admin</th>
                      <th className="border px-3 py-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingUnits.map(u => (
                      <tr key={u.id} className="odd:bg-white even:bg-gray-50">
                        <td className="border px-3 py-2">{u.name}</td>
                        <td className="border px-3 py-2">{clusterNameById(u.cluster_id) || '-'}</td>
                        <td className="border px-3 py-2">
                          {editUnitId === u.id ? (
                            <input className="border rounded p-2 w-full" value={editUnitPhone} onChange={e=>setEditUnitPhone(e.target.value)} />
                          ) : (
                            <span>{u.phone || '-'}</span>
                          )}
                        </td>
                        <td className="border px-3 py-2">
                          {editUnitId === u.id ? (
                            <input className="border rounded p-2 w-full" value={editUnitAddress} onChange={e=>setEditUnitAddress(e.target.value)} />
                          ) : (
                            <span>{u.address || '-'}</span>
                          )}
                        </td>
                        <td className="border px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">{u.unit_admin_name || '-'}</span>
                            <button className="px-2 py-1 text-sm bg-gray-100 rounded text-gray-700" onClick={()=>openAssignForUnit(u)}>Assign</button>
                            {u.unit_admin_name && (
                              <button className="px-2 py-1 text-sm bg-red-50 text-red-700 rounded" onClick={()=>removeUnitAdmin(u)}>Remove</button>
                            )}
                          </div>
                        </td>
                        <td className="border px-3 py-2">
                          {editUnitId === u.id ? (
                            <div className="flex gap-2">
                              <button className="px-2 py-1 text-sm bg-green-600 text-white rounded" onClick={()=>saveEditUnit(u)}>Save</button>
                              <button className="px-2 py-1 text-sm bg-gray-200 rounded" onClick={cancelEditUnit}>Cancel</button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button className="px-2 py-1 text-sm bg-blue-600 text-white rounded" onClick={()=>startEditUnit(u)}>Edit</button>
                              <button className="px-2 py-1 text-sm bg-red-600 text-white rounded" onClick={()=>deleteUnit(u)}>Delete</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {existingUnits.length === 0 && (
                      <tr>
                        <td className="border px-3 py-4 text-center text-gray-500" colSpan={6}>No units found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-gray-500">Select a company to manage units.</div>
          )}
        </Section>
      )}
      {/* Company Create/Edit Modal */}
      {showCompanyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={()=>{ setShowCompanyModal(false); setIsEditingCompanyDetails(false); }} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{isNewCompany ? 'Create Company' : 'Edit Company'}</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={()=>{ setShowCompanyModal(false); setIsEditingCompanyDetails(false); }}>Close</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-1">Company Name</label>
                <input className="border rounded p-2 w-full" value={companyName} onChange={e=>setCompanyName(e.target.value)} placeholder="Enter company name" />
                {nameCheckStatus==='checking' && <div className="text-xs text-gray-500 mt-1">Checking name...</div>}
                {nameCheckStatus==='duplicate' && <div className="text-xs text-red-600 mt-1">A company with this name already exists.</div>}
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Industry</label>
                <input className="border rounded p-2 w-full" value={companyIndustry} onChange={e=>setCompanyIndustry(e.target.value)} placeholder="e.g., Technology, Healthcare" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Employees</label>
                <select className="border rounded p-2 w-full" value={companyEmployeeCount} onChange={e=>setCompanyEmployeeCount(e.target.value)}>
                  <option value="">Select size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-1000">201-1000 employees</option>
                  <option value="1000+">1000+ employees</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Website</label>
                <input className="border rounded p-2 w-full" value={companyWebsite} onChange={e=>setCompanyWebsite(e.target.value)} placeholder="https://www.example.com" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Phone</label>
                <input className="border rounded p-2 w-full" value={companyPhone} onChange={e=>setCompanyPhone(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-1">Address</label>
                <textarea className="border rounded p-2 w-full" rows={3} value={companyAddress} onChange={e=>setCompanyAddress(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-1">Company Logo (optional)</label>
                <input type="file" accept="image/*" onChange={e=>setCompanyLogoFile(e.target.files?.[0] || null)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-1">Description</label>
                <textarea className="border rounded p-2 w-full" rows={3} value={companyDescription} onChange={e=>setCompanyDescription(e.target.value)} />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              {isNewCompany ? (
                <button disabled={!companyName.trim() || saving || nameCheckStatus==='duplicate'} className="px-4 py-2 rounded bg-indigo-600 text-white disabled:opacity-50" onClick={async()=>{ await createCompanyAndSetup(); if (!error) { setShowCompanyModal(false); } }}>
                  {saving ? 'Creating...' : 'Create Company'}
                </button>
              ) : (
                <>
                  <button className="px-4 py-2 rounded bg-gray-100 text-gray-800" onClick={uploadExistingCompanyLogo}>Upload Logo</button>
                  <button disabled={saving || nameCheckStatus==='duplicate'} className="px-4 py-2 rounded bg-indigo-600 text-white disabled:opacity-50" onClick={async()=>{ await saveExistingCompanyUpdates(); if (!error) { setShowCompanyModal(false); setIsEditingCompanyDetails(false); } }}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Assign Admin Modal */}
      <AdminAssignModal
        open={assignOpen}
        onClose={()=>setAssignOpen(false)}
        onSubmit={submitAssignAdmin}
        submitting={assignSubmitting}
        title={assignTarget?.scope ? `Assign ${assignTarget.scope.charAt(0).toUpperCase()+assignTarget.scope.slice(1)} Admin` : 'Assign Admin'}
        initial={assignTarget?.initial}
      />
    </div>
  );
}
